import { setupLayouts } from 'virtual:generated-layouts'
import { defineComponent, h } from 'vue'
import type { NavigationGuardNext, RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'

import { useAuthStore } from '@/modules/authStore/stores/authStore'
import { resolveStaleDemoRoute } from '@/modules/onboarding/utils/demoRoute'
import { useUserStore } from '@/modules/user/stores/userStore'
import { logtoClient } from '@/plugins/logto'
import { ERouteNames } from '@/router/ERouteNames'
import { logger } from '@/utils/eventLogger'

import PageNotFound from '@/components/createdUi/PageNotFound.vue'
import ServiceWorks from '@/components/createdUi/ServiceWorks.vue'
import AuditLayout from '@/modules/audits/components/AuditLayout.vue'
import CalendarModule from '@/modules/calendar/components/CalendarModule.vue'
import DashboardPage from '@/modules/dashboard/components/DashboardPage.vue'
import PpuSettings from '@/modules/improvements/components/PpuSettings.vue'
import ImprovementFormModal from '@/modules/improvements/components/ImprovementFormModal.vue'
import ImprovementsLayout from '@/modules/improvements/components/ImprovementsLayout.vue'
import RegistrPPU from '@/modules/improvements/components/RegistrPPU.vue'
import SuggestionsList from '@/modules/improvements/components/SuggestionsList.vue'
import SuggestionsRating from '@/modules/improvements/components/SuggestionsRating.vue'
import LinksLayout from '@/modules/links/components/LinksLayout.vue'
import LinksSettings from '@/modules/links/components/LinksSettings.vue'
import UsefulLinks from '@/modules/links/components/UsefulLinks.vue'
import MentorshipCaseDialog from '@/modules/mentorship/components/MentorshipCaseDialog.vue'
import MentorshipCasesPage from '@/modules/mentorship/components/MentorshipCasesPage.vue'
import MentorshipSettingsPage from '@/modules/mentorship/components/MentorshipSettingsPage.vue'
import ProgramTemplatesPage from '@/modules/mentorship/components/ProgramTemplatesPage.vue'
import PortalSettingsPage from '@/modules/settings/components/PortalSettingsPage.vue'
import { canAccessPortalSettings } from '@/modules/settings/utils/portalSettingsAccess'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import TasksPage from '@/modules/tasks/components/TasksPage.vue'
import TrainingPage from '@/modules/training/components/TrainingPage.vue'
import TicketForm from '@/modules/tickets/components/TicketForm.vue'
import TicketsTable from '@/modules/tickets/components/TicketsTable.vue'
import UniversalStatsTable from '@/modules/universalStats/components/UniversalStatsTable.vue'
import AdverseReports from '@/modules/adverseEvents/components/AdverseReports.vue'
import UserProfile from '@/modules/user/components/UserProfile.vue'
import LogtoCallback from '@/pages/logto-callback.vue'
import AdverseEvents from '@/views/AdverseEvents/AdverseEvents.vue'
import DocumentsNew from '@/views/apps/documents-new/index.vue'
import JournalDetail from '@/views/apps/journals/components/journal-detail/JournalDetail.vue'
import JournalsIndex from '@/views/apps/journals/index.vue'
import CitizenInquiriesTable from '@/modules/citizenInquiries/components/CitizenInquiriesTable.vue'
import CitizenInquiryFormModal from '@/modules/citizenInquiries/components/CitizenInquiryFormModal.vue'
import CitizenInquiriesSettingsPage from '@/modules/citizenInquiries/components/settings/CitizenInquiriesSettingsPage.vue'

// Аудиты
import AuditRegisterTable from '@/modules/audits/stores/useAuditRegisterStore/components/AuditRegisterTable.vue'
import FormAudit from '@/modules/audits/stores/useAuditRegisterStore/components/FormAudit.vue'
import AuditCard from '@/modules/audits/stores/useAuditRegisterStore/components/AuditCard.vue'
import AuditProcessCard from '@/modules/audits/stores/useAuditRegisterStore/components/AuditProcessCard.vue'
import AuditNonconformitiesByAuditPage from '@/modules/audits/stores/useAuditNonconfrormitiesStore/components/AuditNonconformitiesByAuditPage.vue'
import AuditNonconformityForm from '@/modules/audits/stores/useAuditNonconformityFormStore/components/AuditNonconformityForm.vue'
import AuditChart from '@/modules/audits/stores/useAuditCalendarStore/components/AuditChart.vue'
import AuditNonconfrormitiesTable from '@/modules/audits/stores/useAuditNonconfrormitiesStore/components/AuditNonconfrormitiesTable.vue'
import AuditArchiveTable from '@/modules/audits/stores/useAuditArchiveStore/components/AuditArchiveTable.vue'
import AuditGroupTable from '@/modules/audits/stores/useAuditGroupStore/components/AuditGroupTable.vue'
import FormAuditGroup from '@/modules/audits/stores/useAuditGroupStore/components/FormAuditGroup.vue'

// Персонал
import StaffTable from '@/modules/staff/components/StaffTable.vue'
import StaffModuleSettingsPage from '@/modules/staff/components/moduleSettings/StaffModuleSettingsPage.vue'
import ImportDataPage from '@/modules/import/components/ImportDataPage.vue'
import EmployeeActivity from '@/modules/staff/components/EmployeeActivity.vue'

// Заявки
import WorkGroupsTable from '@/modules/tickets/components/WorkGroupsTable.vue'

// Уведомления
import NotificationCenterPage from '@/modules/notifications/pages/NotificationCenterPage.vue'
import NotificationSettingsPage from '@/modules/notifications/pages/NotificationSettingsPage.vue'

// Дашборд / Графики
import UniversalGraph from '@/modules/dashboard/components/UniversalGraph.vue'

// Импорты из Materio

// === Заглушка компонента для всех маршрутов (кроме логина)
const RouteStub = defineComponent({
  name: 'RouteStub',
  setup() {
    return () =>
      h('div', { class: 'route-stub' }, 'Заглушка компонента — временно')
  },
})

const asyncStub = () => Promise.resolve(RouteStub)

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: ERouteNames.LOGIN,
    component: () => import('@/pages/login.vue'),
    meta: {
      requiresAuth: false,
      name: 'Авторизация',
      skipLayout: true,
    },
  },
  {
    path: '/callback',
    name: ERouteNames.LOGTO_CALLBACK,
    component: LogtoCallback,
    meta: {
      requiresAuth: false,
      name: 'Logto Callback',
      skipLayout: true,
    },
  },
  {
    path: '/matrix-callback',
    name: ERouteNames.MATRIX_CALLBACK,
    component: () => import('@/pages/matrix-callback.vue'),
    meta: {
      requiresAuth: false,
      name: 'Matrix Callback',
      skipLayout: true,
    },
  },
  {
    path: '/service-works',
    name: ERouteNames.SERVICE_WORKS,
    component: ServiceWorks,
    meta: {
      skipLayout: true,
    },
  },
  {
    path: '/404',
    name: ERouteNames.PAGE_NOT_FOUND,
    component: PageNotFound,
    meta: {
      skipLayout: true,
    },
  },
  {
    path: '/',
    name: ERouteNames.HOME,
    component: () => import('@/pages/LandingPage.vue'),
    meta: { requiresAuth: true },
    redirect: { name: ERouteNames.WORKSPACE },
    children: [
      {
        path: 'workspace',
        name: ERouteNames.WORKSPACE,
        component: () => import('@/modules/workspace/components/WorkspacePage.vue'),
        meta: { name: 'Рабочий стол' },
      },
      {
        path: 'documents',
        name: ERouteNames.DOCUMENTS,
        component: DocumentsNew,
        meta: { name: 'Документы' },
      },
      {
        path: 'documents/folder/:folderId',
        name: ERouteNames.DOCUMENTS_FOLDER,
        component: DocumentsNew,
        meta: { name: 'Документы', navActiveLink: ERouteNames.DOCUMENTS },
        props: true,
      },
      {
        path: 'tickets',
        name: ERouteNames.TICKETS,
        component: TicketsTable,
        meta: { name: 'Тикеты' },
        children: [
          {
            path: 'create',
            name: ERouteNames.CREATE_TICKET,
            component: TicketForm,
          },
          {
            path: ':id',
            name: ERouteNames.TICKET_DETAIL,
            component: TicketForm,
          },
        ],
      },
      {
        path: 'equipment',
        name: ERouteNames.EQUIPMENT,
        component: () => import('@/modules/equipment/components/EquipmentPage.vue'),
        meta: { name: 'Оборудование' },
        children: [
          { path: 'create', name: ERouteNames.EQUIPMENT_CREATE, component: () => import('@/modules/equipment/components/EquipmentCard.vue'), meta: { permission: 'equipment.edit' } },
          { path: ':id', name: ERouteNames.EQUIPMENT_DETAIL, component: () => import('@/modules/equipment/components/EquipmentCard.vue') },
        ],
      },
      {
        path: 'tickets/work-groups',
        name: ERouteNames.TICKETS_WORK_GROUPS,
        component: WorkGroupsTable,
        meta: { name: 'Справочник групп исполнителей', navActiveLink: ERouteNames.TICKETS, permission: 'ticket.work_group_edit' },
      },
      {
        path: 'citizen-inquiries',
        name: ERouteNames.CITIZEN_INQUIRIES,
        component: CitizenInquiriesTable,
        // Бэк закрывает весь модуль правом appeals.view (CanViewAppeals) — без него реестр отдаёт 403
        meta: { name: 'Обращения граждан', permission: 'appeals.view' },
        children: [
          {
            path: 'create',
            name: ERouteNames.CITIZEN_INQUIRY_CREATE,
            component: CitizenInquiryFormModal,
            meta: { name: 'Создание обращения', navActiveLink: ERouteNames.CITIZEN_INQUIRIES, permission: 'appeals.create' },
          },
          {
            path: ':id',
            name: ERouteNames.CITIZEN_INQUIRY_DETAIL,
            component: CitizenInquiryFormModal,
            // permission: undefined — снимаем унаследованное appeals.view: карточку может открыть участник задачи, доступ решает бэк
            meta: { name: 'Обращение гражданина', navActiveLink: ERouteNames.CITIZEN_INQUIRIES, permission: undefined },
          },
        ],
      },
      {
        path: 'citizen-inquiries/settings',
        name: ERouteNames.CITIZEN_INQUIRIES_SETTINGS,
        component: CitizenInquiriesSettingsPage,
        meta: {
          name: 'Настройки модуля Обращения граждан',
          navActiveLink: ERouteNames.CITIZEN_INQUIRIES,
          permission: 'appeals.channel_edit',
        },
      },
      {
        path: 'electronic-signatures/:collection?',
        name: ERouteNames.ELECTRONIC_SIGNATURES,
        component: () => import('@/modules/electronicSignatures/components/SignatureRegistry.vue'),
        meta: { name: 'Электронные подписи' },
      },
      {
        path: 'electronic-signatures/cases/:id',
        name: ERouteNames.ELECTRONIC_SIGNATURE_CASE,
        component: () => import('@/modules/electronicSignatures/components/SignatureRegistry.vue'),
        meta: { name: 'Заявка электронной подписи', navActiveLink: ERouteNames.ELECTRONIC_SIGNATURES },
      },
      {
        path: 'tasks',
        name: ERouteNames.TASKS,
        component: TasksPage,
        meta: { name: 'Задачи' },
      },
      {
        path: 'mentorship',
        name: ERouteNames.MENTORSHIP_CASES,
        component: MentorshipCasesPage,
        meta: { name: 'Наставничество' },
        children: [
          {
            path: 'create',
            name: ERouteNames.MENTORSHIP_CASE_CREATE,
            component: MentorshipCaseDialog,
            meta: { name: 'Новое дело наставничества', navActiveLink: ERouteNames.MENTORSHIP_CASES },
          },
          {
            path: ':id',
            name: ERouteNames.MENTORSHIP_CASE_DETAIL,
            component: MentorshipCaseDialog,
            meta: { name: 'Дело наставничества', navActiveLink: ERouteNames.MENTORSHIP_CASES },
          },
          {
            path: 'settings',
            name: ERouteNames.MENTORSHIP_SETTINGS,
            component: MentorshipSettingsPage,
            meta: { name: 'Настройки наставничества', navActiveLink: ERouteNames.MENTORSHIP_CASES },
          },
          {
            path: 'templates',
            name: ERouteNames.MENTORSHIP_TEMPLATES,
            component: ProgramTemplatesPage,
            meta: { name: 'Программы наставничества', navActiveLink: ERouteNames.MENTORSHIP_CASES },
          },
        ],
      },
      {
        path: 'training',
        name: ERouteNames.TRAINING,
        component: TrainingPage,
        meta: { name: 'Обучение СМК' },
      },
      {
        path: 'portal-settings',
        name: ERouteNames.PORTAL_SETTINGS,
        component: PortalSettingsPage,
        meta: { name: 'Настройки портала', requiresPortalAdministrator: true },
      },
      {
        path: 'profile',
        name: ERouteNames.USER_PROFILE,
        component: UserProfile,
      },
      {
        path: 'notifications',
        name: ERouteNames.NOTIFICATION_CENTER,
        component: NotificationCenterPage,
        meta: { name: 'Центр уведомлений' },
      },
      {
        path: 'notifications/settings',
        name: ERouteNames.NOTIFICATION_SETTINGS,
        component: NotificationSettingsPage,
        meta: { name: 'Настройка уведомлений', navActiveLink: ERouteNames.NOTIFICATION_CENTER },
      },
      {
        path: 'staff',
        name: ERouteNames.STAFF,
        component: StaffTable,
      },
      {
        path: 'staff/settings',
        name: ERouteNames.STAFF_SETTINGS,
        component: StaffModuleSettingsPage,
        meta: {
          name: 'Настройки модуля Персонал',
          navActiveLink: ERouteNames.STAFF,
          permission: ['employee_management.department_edit', 'employee_management.position_edit'],
        },
      },
      {
        path: 'import/:importType',
        name: ERouteNames.IMPORT_DATA,
        component: ImportDataPage,
        meta: { name: 'Импортирование данных' },
      },
      {
        path: 'staff/:id/activity',
        name: ERouteNames.STAFF_ACTIVITY,
        component: EmployeeActivity,
        props: true,
        meta: { name: 'Активность пользователя', navActiveLink: ERouteNames.STAFF, permission: 'employee_management.employee_view' },
      },
      {
        path: 'staff-graph',
        name: ERouteNames.STAFF_GRAPH,
        component: UniversalGraph,
        props: { target: 'employee' },
        meta: { navActiveLink: ERouteNames.STAFF },
      },
      {
        path: 'journals',
        name: ERouteNames.JOURNALS,
        component: JournalsIndex,
        meta: { name: 'Журналы' },
      },
      {
        path: 'journals/:id',
        name: ERouteNames.JOURNAL_DETAIL,
        component: JournalDetail,
        meta: { name: 'Детали журнала' },
        props: true,
      },
      {
        path: 'links',
        component: LinksLayout,
        meta: { navActiveLink: ERouteNames.LINKS },
        children: [
          {
            path: '',
            name: ERouteNames.LINKS,
            component: UsefulLinks,
          },
          {
            path: 'settings',
            name: ERouteNames.LINKS_SETTINGS,
            component: LinksSettings,
            meta: { permission: 'links.edit' },
          },
        ],
      },
      {
        path: 'audits',
        component: AuditLayout,
        meta: { navActiveLink: ERouteNames.AUDITS },
        children: [
          {
            path: '',
            name: ERouteNames.AUDITS,
            component: AuditRegisterTable,
            children: [
              {
                path: 'create',
                name: ERouteNames.AUDIT_CREATE,
                component: FormAudit,
              },
              {
                path: ':id',
                name: ERouteNames.AUDIT_DETAIL,
                component: AuditCard,
              },
              {
                path: ':id/process/:departmentId',
                name: ERouteNames.AUDIT_PROCESS,
                component: AuditProcessCard,
              },
              {
                path: ':id/nonconformities',
                name: ERouteNames.AUDIT_NONCONFORMITIES,
                component: AuditNonconformitiesByAuditPage,
                children: [
                  {
                    path: ':reportId',
                    name: ERouteNames.AUDIT_NONCONFORMITY_DETAIL,
                    component: AuditNonconformityForm,
                  },
                ],
              },
            ],
          },
          {
            path: 'charts',
            name: ERouteNames.AUDITS_CHART,
            component: AuditChart,
          },
          {
            path: 'nonconformities',
            name: ERouteNames.AUDITS_NONCONFORMITIES,
            component: AuditNonconfrormitiesTable,
            children: [
              {
                path: ':reportId',
                name: ERouteNames.AUDITS_NONCONFORMITY_DETAIL,
                component: AuditNonconformityForm,
              },
            ],
          },
          {
            path: 'statistics',
            name: ERouteNames.AUDITS_GRAPH,
            component: UniversalGraph,
            props: { target: 'audit' },
          },
          {
            path: 'archive',
            name: ERouteNames.AUDITS_ARCHIVE,
            component: AuditArchiveTable,
          },
          {
            path: 'group',
            name: ERouteNames.AUDITS_GROUP,
            component: AuditGroupTable,
            children: [
              {
                path: 'create',
                name: ERouteNames.AUDIT_GROUP_CREATE,
                component: FormAuditGroup,
              },
              {
                path: ':id',
                name: ERouteNames.AUDIT_GROUP_DETAIL,
                component: FormAuditGroup,
              },
            ],
          },
        ],
      },
      {
        path: 'news',
        name: ERouteNames.NEWS,
        component: () => import('@/views/apps/actual/index.vue'),
        meta: { name: 'Актуальное' },
      },
      {
        path: 'news/:id',
        name: ERouteNames.NEWS_DETAIL,
        component: () => import('@/views/apps/actual/ActualDetail.vue'),
        props: true,
        meta: { name: 'Детали новости' },
      },
      {
        path: 'news/create',
        name: ERouteNames.NEWS_CREATE,
        component: asyncStub,
        meta: { requiresAuth: true, requiresManager: true },
      },
      {
        path: 'news/edit/:id',
        name: ERouteNames.NEWS_EDIT,
        component: asyncStub,
        props: true,
        meta: { requiresAuth: true, requiresManager: true },
      },
      {
        path: 'monitoring',
        name: ERouteNames.MONITORING,
        component: DashboardPage,
      },
      // Совместимость существующих ссылок на прежний раздел чата.
      {
        path: 'chat/:param?',
        redirect: to => ({
          name: ERouteNames.CHAT_MATRIX,
          params: to.params.param ? { param: to.params.param } : {},
          query: to.query,
          hash: to.hash,
        }),
      },
      {
        path: 'messenger/:param?',
        name: ERouteNames.CHAT_MATRIX,
        component: () => import('@/pages/apps/chat-matrix-fast.vue'),
        props: route => ({
          userId: route.params.param || undefined,
        }),
        meta: { requiresAuth: true },
      },
      {
        path: 'messenger-fast/:param?',
        redirect: to => ({
          name: ERouteNames.CHAT_MATRIX,
          params: to.params.param ? { param: to.params.param } : {},
          query: to.query,
          hash: to.hash,
        }),
      },
      {
        path: 'chat-matrix-fast/:param?',
        redirect: to => ({
          name: ERouteNames.CHAT_MATRIX,
          params: to.params.param ? { param: to.params.param } : {},
          query: to.query,
          hash: to.hash,
        }),
      },
      {
        path: 'chat-matrix/:param?',
        redirect: to => ({
          name: ERouteNames.CHAT_MATRIX,
          params: to.params.param ? { param: to.params.param } : {},
          query: to.query,
          hash: to.hash,
        }),
      },
      {
        path: 'improvements',
        component: ImprovementsLayout,
        meta: { navActiveLink: ERouteNames.IMPROVEMENTS },
        children: [
          {
            path: '',
            name: ERouteNames.IMPROVEMENTS,
            component: SuggestionsList,
            children: [
              {
                path: ':id',
                name: ERouteNames.IMPROVEMENT_DETAIL,
                component: ImprovementFormModal,
              },
            ],
          },
          {
            path: 'registry',
            name: ERouteNames.IMPROVEMENTS_REGISTRY,
            component: RegistrPPU,
            meta: { permission: 'improvements.manage' },
          },
          {
            path: 'ppusettings',
            name: ERouteNames.IMPROVEMENTS_COMMISSION,
            component: PpuSettings,
            meta: { permission: 'improvements.manage' },
          },
          {
            path: 'rating',
            name: ERouteNames.IMPROVEMENTS_RATING,
            component: SuggestionsRating,
            meta: { permission: 'improvements.manage' },
          },
        ],
      },

      {
        path: 'adverse-events',
        name: ERouteNames.ADVERSE_EVENTS,
        component: AdverseEvents,
        children: [
          {
            path: ':id',
            name: ERouteNames.ADVERSE_EVENT_DETAIL,
            component: () => import('@/views/AdverseEvents/FormAdverseEvent.vue'),
          },
          {
            path: 'create',
            name: ERouteNames.CREATE_ADVERSE_EVENT,
            component: () => import('@/views/AdverseEvents/FormAdverseEvent.vue'),
          },
        ],
      },
      {
        path: 'adverse-graph',
        name: ERouteNames.ADVERSE_GRAPH,
        component: UniversalGraph,
        props: { target: 'adverse' },
        meta: { navActiveLink: ERouteNames.ADVERSE_EVENTS },
      },
      {
        path: 'adverse-stats',
        name: ERouteNames.ADVERSE_STATS,
        component: AdverseReports,
        meta: { navActiveLink: ERouteNames.ADVERSE_EVENTS },
      },
      {
        path: 'ticket-graph',
        name: ERouteNames.TICKETS_GRAPH,
        component: UniversalGraph,
        props: { target: 'ticket' },
        meta: { navActiveLink: ERouteNames.TICKETS },
      },
      {
        path: 'ticket-stats',
        name: ERouteNames.TICKETS_STATS,
        component: UniversalStatsTable,
        props: {
          statsType: 'ticket',
        },
        meta: { navActiveLink: ERouteNames.TICKETS },
      },
      {
        path: 'calendar',
        name: ERouteNames.CALENDAR,
        component: CalendarModule,
        meta: { name: 'Календарь' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: ERouteNames.PAGE_NOT_FOUND,
    component: PageNotFound,
    meta: {
      skipLayout: true,
    },
  },
]

const routesWithLayouts = routes.map(route =>
  route.meta?.skipLayout ? route : setupLayouts([route])[0],
)

// Логирование для отладки
/* function logRouteTree(routesArr: RouteRecordRaw[], prefix = '') {
  routesArr.forEach((r) => {
    const comp = (r as any).component || (r as any).components?.default

    const compFile = comp
      ? (comp as any).__file || (comp as any).name || String(comp).slice(0, 200)
      : '(no comp)'

    console.log(
      '[ROUTE]',
      prefix + (r.path || '/'),
      'name:',
      r.name,
      'meta:',
      r.meta,
      'compFile:',
      compFile,
    )
    if (r.children) logRouteTree(r.children, `${prefix + (r.path || '/')}>`)
  })
} */

/* console.log('--- ROUTES WITH LAYOUTS (debug) ---')
logRouteTree(routesWithLayouts)
console.log('------------------------------------') */

// === Создание роутера с интеграцией функционала Materio ===
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routesWithLayouts,
  /**
   * Поведение скролла при навигации.
   *
   * Приоритет источников позиции:
   *   1. `savedPosition` — браузер передаёт при нативных back/forward.
   *   2. Якорь `to.hash` — плавная прокрутка с отступом 60px.
   *   3. Иначе `{ top: 0 }` — обычный переход на новый маршрут.
   */
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    if (to.hash) {
      return { el: to.hash, behavior: 'smooth', top: 60 }
    }

    if (to.path === from.path) {
      return false
    }

    return { top: 0 }
  },
})

/**
 * Проверяет право вида `'<модуль>.<действие>'` в `permissionsStore.modules`.
 */
function hasPermission(permission: string | string[]): boolean {
  const modules = usePermissionsStore().modules as Record<string, Record<string, boolean | undefined> | undefined>
  const list = Array.isArray(permission) ? permission : [permission]

  return list.some(item => {
    const [module, action] = item.split('.')
    return !!modules[module]?.[action]
  })
}

/** Ключи стора. `documents.edit` на бэке — это `document.document_edit`. */
const importTypePermission: Record<string, string> = {
  employees: 'employee_management.employee_edit',
  departments: 'employee_management.department_edit',
  locations: 'hospital.structure_edit',
  documents: 'documents.edit',
  qualitycontrol: 'admin.admin_panel',
}

// Навигационные guards.
// Источник правды об аутентификации — Logto SDK (logtoClient.isAuthenticated()):
// он читает токены из localStorage и проверяет срок. На каждом переходе синхронизируем
// authStore.isAuthenticated с актуальным состоянием SDK. Вызов дешёвый — без сетевых запросов.
router.beforeEach(
  async (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    const authStore = useAuthStore()
    const userStore = useUserStore()

    // Синхронизация флага авторизации с Logto SDK. Если SDK не инициализирован
    // (нет env VITE_LOGTO_ENDPOINT / VITE_LOGTO_APP_ID) — считаем пользователя неавторизованным.
    const isAuth = logtoClient ? await logtoClient.isAuthenticated() : false
    if (!isAuth) authStore.isAuthenticated = false

    if (to.path !== _from.path) {
      logger.info(`[Router] Навигация: ${to.path}. Требуется auth: ${to.meta.requiresAuth ?? false}, Авторизован: ${isAuth}`)
    }

    if (to.meta.requiresAuth && !isAuth) {
      const { rememberEquipmentTarget } = await import('@/modules/equipment/returnTarget')
      rememberEquipmentTarget(to.path)
      logger.info(
        '🔄 Навигация роутера: 🚫 Нет авторизации, перенаправляем на логин /login',
      )
      next('/login')
    } else if (to.path === '/login' && isAuth && authStore.isAuthenticated && userStore.user && to.query.auto !== 'true') {
      logger.info(
        '🔄 Навигация роутера: ✅ Уже авторизован, перенаправляем с логина в портал на /',
      )
      next('/')
    } else if (to.meta.requiresManager && !userStore.user?.is_manager) {
      logger.info(
        '🔄 Навигация роутера: 👨‍💼 Требуется менеджер, перенаправляем на актуальные новости',
      )
      next({ name: ERouteNames.NEWS })
    } else if (to.meta.requiresPortalAdministrator) {
      const permissionsStore = usePermissionsStore()
      if (!permissionsStore.loaded || permissionsStore.loading)
        await permissionsStore.whenReady()
      if (!canAccessPortalSettings(userStore.user)) {
        logger.info(
          'Навигация роутера: для раздела требуется право hospital.settings_edit, перенаправляем на рабочий стол',
        )
        next({ name: ERouteNames.WORKSPACE })
      } else {
        next()
      }
    } else if (to.name === ERouteNames.IMPORT_DATA) {
      const permissionsStore = usePermissionsStore()
      if (!permissionsStore.loaded || permissionsStore.loading)
        await permissionsStore.whenReady()
      const permission = importTypePermission[String(to.params.importType || '')]
      if (permission && !hasPermission(permission)) {
        logger.info(`Навигация роутера: нет права ${permission} для импорта, перенаправляем на рабочий стол`)
        next({ name: ERouteNames.WORKSPACE })
      } else {
        next()
      }
    } else if (to.meta.permission) {
      const permissionsStore = usePermissionsStore()
      if (!permissionsStore.loaded || permissionsStore.loading)
        await permissionsStore.whenReady()
      if (!hasPermission(to.meta.permission)) {
        logger.info(`Навигация роутера: нет права ${to.meta.permission}, перенаправляем в родительский раздел`)
        const parent = to.meta.navActiveLink
        next(typeof parent === 'string' ? { name: parent } : parent ?? { name: ERouteNames.WORKSPACE })
      } else {
        next()
      }
    } else {
      next()
    }
  },
)

router.beforeEach(to => resolveStaleDemoRoute(to))

export default router
